// Types pour les partenaires de lieu - Super Admin

export type ProviderType = 
  | 'hotel' 
  | 'camping' 
  | 'festival' 
  | 'restaurant' 
  | 'gare' 
  | 'aeroport' 
  | 'centre_commercial' 
  | 'autre';

export type ProviderStatus = 
  | 'active' 
  | 'inactive' 
  | 'pending_approval' 
  | 'suspended';

export interface ProviderCategory {
  id: number;
  name: string;
  slug: string;
  icon: string;
  description?: string;
}

export interface LocationProvider {
  id: number;
  name: string;
  /** Slug public pour /objets-trouves/{slug} */
  slug?: string | null;
  /** Chemin public relatif (ex. /objets-trouves/vieilles-charrues) */
  public_url_path?: string | null;
  type: ProviderType; // Gardé pour la compatibilité
  category?: ProviderCategory; // Nouveau champ pour la catégorie
  /** Présent dans les réponses API détaillées (super-admin) */
  provider_type?: {
    id: number;
    name: string;
    slug: string;
    item_name?: string | null;
    default_attributes?: Record<string, { type?: string; label?: string; options?: string[]; required?: boolean }>;
    annual_event?: boolean;
    place_storage_mode?: 'combined' | 'split' | 'central';
  };
  /** Mode effectif (override inclus). */
  place_storage_mode?: 'combined' | 'split' | 'central';
  /** Override partenaire ; null/undefined = hérite du type. */
  place_storage_mode_override?: 'combined' | 'split' | 'central' | null;
  /** Si l’admin existait déjà sur un autre partenaire (réaffectation). */
  admin_reassigned_from?: {
    id: number;
    name: string;
  };
  formatted_type: string;
  status: ProviderStatus;
  formatted_status: string;
  description?: string;
  website?: string;
  phone?: string;
  email?: string;
  /** Boîte dédiée : matching Sherlook Pro + copie des notifications de messages (côté partenaire). */
  notification_recipient_email?: string | null;
  /** Si false, aucun e-mail automatique vers la boîte partenaire. */
  notification_emails_enabled?: boolean;
  /** Catalogue masqué sur le site public ; seul le matching relie les déclarations. */
  private_announcements?: boolean;
  /** Si false, le partenaire n’apparaît pas sur le site public (menu, carousel, fiches). */
  visible_on_frontend?: boolean;
  /** Fond plein écran page publique dédiée (hex #RRGGBB). */
  private_page_background_color?: string | null;
  /** Cadrage logo dans le cercle page privée (scale, offsetX/Y en %). */
  private_page_logo_layout?: PartnerLogoCircleLayout | null;
  logo?: string;
  logo_url?: string;
  config: ProviderConfig;
  settings?: ProviderSettings;
  delivery_modes?: ProviderDeliveryModesSettings;
  monthly_fee?: string;
  /** Ordre dans le menu « Partenaires » du site public */
  public_menu_sort_order?: number;
  created_at: string;
  updated_at?: string;
  activated_at?: string;
  /** Annonces classiques actives + objets trouvés non « disposés » (sans double compter les miroirs FO-CONTACT). */
  active_announcements_count: number;
  classic_active_announcements_count?: number;
  /** Total objets trouvés (tous statuts). */
  found_objects_count?: number;
  /** Objets trouvés hors statut « disposé » — affiché côté admin partenaires. */
  found_objects_active_count?: number;
  address: ProviderAddress;
  /** Null si aucun admin principal (ex. après transfert de compte). */
  admin_user: ProviderAdminUser | null;
}

export interface ProviderAddress {
  id: number;
  name: string;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  postal_code?: string;
  latitude?: number;
  longitude?: number;
}

export interface ProviderAdminUser {
  id: number;
  email: string;
  login_username?: string | null;
  first_name?: string;
  last_name?: string;
}

export interface ProviderConfig {
  [key: string]: any;
}

export interface ProviderSettings {
  [key: string]: any;
}

export interface PartnerLogoCircleLayout {
  scale: number;
  offsetX: number;
  offsetY: number;
}

export interface ProviderInterfaceThemeSettings {
  preset?: string;
  primary?: string;
  secondary?: string;
  background?: string;
}

/** Menus optionnels du dashboard partenaire (Sherlook Pro). */
export interface ProviderDashboardMenuSettings {
  matchings_enabled?: boolean;
  linked_announcements_enabled?: boolean;
}

export type ProviderDeliveryModeKey =
  | 'colissimo_home'
  | 'colissimo_pickup'
  | 'chronopost_relais_13'
  | 'chronopost_shop2shop'
  | 'chronopost_relais_europe'
  | 'chronopost_relais_dom'
  | 'chronopost_home_13'
  | 'chronopost_home_10'
  | 'chronopost_home_18'
  | 'mondial_relay_pickup';

export type ProviderDeliveryModesSettings = Partial<Record<ProviderDeliveryModeKey, boolean>>;

export const PROVIDER_DELIVERY_MODE_OPTIONS: Array<{ key: ProviderDeliveryModeKey; label: string }> = [
  { key: 'colissimo_home', label: 'Colissimo — livraison à domicile' },
  { key: 'colissimo_pickup', label: 'Colissimo — point relais' },
  { key: 'chronopost_relais_13', label: 'Chronopost — Relais 13 (5X)' },
  { key: 'chronopost_shop2shop', label: 'Chronopost — Shop2Shop (86)' },
  { key: 'chronopost_relais_europe', label: 'Chronopost — Relais Europe (49)' },
  { key: 'chronopost_relais_dom', label: 'Chronopost — Relais DOM (4P)' },
  { key: 'chronopost_home_13', label: 'Chronopost — Chrono 13 domicile (9A)' },
  { key: 'chronopost_home_10', label: 'Chronopost — Chrono 10 domicile (9B)' },
  { key: 'chronopost_home_18', label: 'Chronopost — Chrono 18 domicile (9C)' },
  { key: 'mondial_relay_pickup', label: 'Mondial Relay — Point Relais / Locker' },
];

export const DEFAULT_PROVIDER_DASHBOARD_MENU: ProviderDashboardMenuSettings = {
  matchings_enabled: true,
  linked_announcements_enabled: true,
};

// Types pour les requêtes API
export interface CreateProviderRequest {
  name: string;
  /** Slug du type (ex. hotel) — doit exister dans la table provider_type si pas de provider_type_id */
  type: string;
  /** ID de l'entité ProviderType (prioritaire sur type) */
  provider_type_id?: number;
  category_id?: number; // Nouveau champ pour l'ID de la catégorie
  description?: string;
  website?: string;
  phone?: string;
  email?: string;
  config?: ProviderConfig;
  settings?: ProviderSettings;
  monthly_fee?: string;
  status?: ProviderStatus;
  admin_email: string;
  admin_first_name?: string;
  admin_last_name?: string;
  address: {
    name: string;
    address?: string;
    city?: string;
    region?: string;
    country?: string;
    postal_code?: string;
    latitude?: number;
    longitude?: number;
  };
}

export interface UpdateProviderRequest {
  name?: string;
  slug?: string | null;
  type?: string;
  provider_type_id?: number | null;
  place_storage_mode_override?: 'combined' | 'split' | 'central' | null;
  category_id?: number; // Nouveau champ pour l'ID de la catégorie
  description?: string;
  website?: string;
  phone?: string;
  email?: string;
  notification_recipient_email?: string | null;
  notification_emails_enabled?: boolean;
  private_announcements?: boolean;
  visible_on_frontend?: boolean;
  private_page_background_color?: string | null;
  private_page_logo_layout?: PartnerLogoCircleLayout | null;
  config?: ProviderConfig;
  settings?: ProviderSettings;
  monthly_fee?: string;
  status?: ProviderStatus;
  /** Mise à jour de l’adresse / coordonnées (super-admin ou admin du lieu) */
  address?: {
    name?: string;
    address?: string;
    city?: string;
    region?: string;
    country?: string;
    postal_code?: string;
    latitude?: number | null;
    longitude?: number | null;
  };
}

export interface ProviderFilters {
  type?: ProviderType;
  status?: ProviderStatus;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ProviderListResponse {
  providers: LocationProvider[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface ProviderStatistics {
  total: number;
  active: number;
  pending: number;
  inactive: number;
  suspended: number;
  by_type: Array<{
    type: ProviderType;
    count: number;
  }>;
}

export interface ProviderTypeInfo {
  name: string;
  icon: string;
  description: string;
}

export interface ProviderTypesResponse {
  types: Record<ProviderType, ProviderTypeInfo>;
}

// Utilitaires pour les types de partenaires
export const PROVIDER_TYPE_LABELS: Record<ProviderType, string> = {
  hotel: 'Hôtel',
  camping: 'Camping',
  festival: 'Festival',
  restaurant: 'Restaurant',
  gare: 'Gare',
  aeroport: 'Aéroport',
  centre_commercial: 'Centre Commercial',
  autre: 'Autre'
};

export const PROVIDER_STATUS_LABELS: Record<ProviderStatus, string> = {
  active: 'Actif',
  inactive: 'Inactif',
  pending_approval: 'En attente',
  suspended: 'Suspendu'
};

export const PROVIDER_TYPE_ICONS: Record<ProviderType, string> = {
  hotel: 'hotel',
  camping: 'camping',
  festival: 'festival',
  restaurant: 'restaurant',
  gare: 'train',
  aeroport: 'flight',
  centre_commercial: 'shopping',
  autre: 'place'
};

// Fonctions utilitaires
export function getProviderTypeLabel(type: ProviderType): string {
  return PROVIDER_TYPE_LABELS[type] || type;
}

export function getProviderStatusLabel(status: ProviderStatus): string {
  return PROVIDER_STATUS_LABELS[status] || status;
}

export function getProviderTypeIcon(type: ProviderType): string {
  return PROVIDER_TYPE_ICONS[type] || 'place';
}

export function isProviderActive(provider: LocationProvider): boolean {
  return provider.status === 'active';
}

export function isProviderPending(provider: LocationProvider): boolean {
  return provider.status === 'pending_approval';
}

